import React from 'react';
import { Box, Button, Typography, useTheme, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OfferStatus, PotentialOffer, TruckDetails } from '../../../types';
import { useAnalytics } from '../../../hooks';

type ActionType = 'accept' | 'orderConfirm' | 'abort' | 'start' | 'phone' | 'close' | 'none';
type ActionResult = ActionType | ActionType[];

interface OfferActionButtonProps {
  offer: PotentialOffer;
  vehicle: TruckDetails | null;
  onRefresh: () => void;
  onAccept: (vehicle: TruckDetails | null, offer: PotentialOffer) => Promise<void>;
  onOrderConfirm: (vehicle: TruckDetails | null, offer: PotentialOffer) => Promise<void>;
  onAbort: (vehicle: TruckDetails | null, offer: PotentialOffer) => Promise<void>;
  onClose: (offer: PotentialOffer) => Promise<void>;
  onView?: (vehicle: TruckDetails | null, offer: PotentialOffer) => Promise<void>;
  onStart?: (offer: PotentialOffer) => void;
}

const INACTIVE_STATUSES = new Set([
  OfferStatus.NEGOTIATION_DECLINED,
  OfferStatus.NEGOTIATION_MANUALLY_CLOSED,
  OfferStatus.NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED,
  OfferStatus.NEGOTIATION_BACK_CONFIRMED,
]);

const getActionType = (offer: PotentialOffer): ActionResult => {
  if (INACTIVE_STATUSES.has(offer.status)) return 'none';
  
  switch (offer.status) {
    case OfferStatus.NEGOTIATION_NEED_HUMAN:
      return ['accept', 'abort'];
    case OfferStatus.NEGOTIATION_WAITING_FINAL_CONFIRM:
      return ['accept', 'abort'];
    case OfferStatus.NEGOTIATION_ACCEPTED:
      return ['orderConfirm', 'abort'];
    case OfferStatus.NEGOTIATION_STARTED:
      return ['close'];
    default:
    // if there is no contact person email, we could start automatic negotiations
      if (!offer.contactPerson?.email?.length) {
        return offer.contactPerson?.phone ? 'phone' : 'none';
      }
      return offer.negotiations?.length ? 'abort' : 'start';
  }
};

const OfferActionButton: React.FC<OfferActionButtonProps> = ({
  offer,
  vehicle,
  onRefresh,
  onAccept,
  onOrderConfirm,
  onAbort,
  onClose,
  onStart,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { trackFeatureUsage, trackAIAutomation } = useAnalytics();
  const actionType = getActionType(offer);

  const stopPropagation = (handler: () => void | Promise<void>) => async (e: React.MouseEvent) => {
    e.stopPropagation();
    await handler();
  };

  const trackAndExecute = (action: string, handler: () => void | Promise<void>) => async () => {
    trackFeatureUsage('offer_action', action, { offer_id: offer.id, status: offer.status });
    if (action === 'order_confirm') {
      trackAIAutomation('booking_confirmed', { offer_id: offer.id });
    }
    await handler();
  };

  const btnSx = { minWidth: '5.5rem' };

  const actionConfig: Record<ActionType, React.ReactNode> = {
    none: (
      <Typography variant="body2" sx={{ color: 'text.grey', fontSize: (theme as any).typography.fontSizes.small }}>
        {t('No actions available')}
      </Typography>
    ),
    accept: (
      <Button
        variant='contained'
        onClick={stopPropagation(trackAndExecute('accept', async () => {await onAccept(vehicle, offer);}))}
        loading={offer.isConfirming}
        size='small'
        color='success'
        sx={btnSx}
      >
        {t('Accept')}
      </Button>
    ),
    orderConfirm: (
      offer.negotiations && offer.negotiations.length > 0 && offer.negotiations[offer.negotiations.length - 1].hasUnsent ? (
        <Tooltip
          title={t('Cannot order confirm while there are unsent messages. Please send or discard them first.')}
          arrow
          placement="top"
        >
          <span>
            <Button
              variant='contained'
              disabled
              size='small'
              color='success'
              sx={{ ...btnSx, pointerEvents: 'none' }}
            >
              {t('Order Confirm')}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant='contained'
          onClick={stopPropagation(trackAndExecute('order_confirm', async () => {await onOrderConfirm(vehicle, offer); onRefresh();}))}
          loading={offer.isConfirming}
          size='small'
          color='success'
          sx={btnSx}
        >
          {t('Order Confirm')}
        </Button>
      )
    ),
    abort: (
      <Button
        variant='outlined'
        onClick={stopPropagation(trackAndExecute('abort', async () => {await onAbort(vehicle, offer); onRefresh();}))}
        loading={offer.isConfirming}
        size='small'
        color='warning'
        sx={btnSx}
      >
        {t('Abort')}
      </Button>
    ),
    close: (
      <Button
        variant='outlined'
        onClick={stopPropagation(trackAndExecute('close', async () => {
          await onClose(offer);
        }))}
        loading={offer.isClosing}
        size='small'
        color='warning'
        sx={btnSx}
      >
        {t('Close')}
      </Button>
    ),
    start: (
      <Button
        variant='outlined'
        onClick={stopPropagation(trackAndExecute('start_negotiation', async () => onStart?.(offer)))}
        size='small'
        color='success'
        sx={btnSx}
      >
        {t('Start')}
      </Button>
    ),
    phone: (
      <Button
        onClick={stopPropagation(trackAndExecute('call', () => { window.location.href = offer.contactPerson?.phone ? `tel:${offer.contactPerson.phone}` : ''; }))}
        size='small'
        color='info'
        sx={btnSx}
      >
        {t('Call')}
      </Button>
    ),
  };

  // Handle array of actions
  const actions = Array.isArray(actionType) ? actionType : [actionType];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
      {actions.map((action, index) => (
        <React.Fragment key={index}>
          {actionConfig[action]}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default OfferActionButton;
